import React, { useState } from "react";
import {
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    FormFeedback,
    FormText,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "reactstrap";
import classnames from "classnames";
import PhoneInput from "../../../components/Forms/PhoneInput";
import { useCompanySettings } from "../hooks/useCompanySettings";
import { BRANDING_ACCEPT_ATTR, formatAllowedDimensions } from "../../../constants/brandingAssets";

const SECTION_TABS = { company: "1", currency: "2", contracts: "3" };

export default function CompanySettingsTab({ companyId, section = "company" }) {
    const {
        formik,
        loading,
        sampleLoading,
        isCorporate,
        logoBlobUrl,
        logoError,
        faviconBlobUrl,
        faviconError,
        handleImageUpload,
        handleImageDelete,
        downloadLogo,
        downloadFavicon,
        loadSampleContract
    } = useCompanySettings(companyId);

    // Tab state yönetimi
    const [activeTab, setActiveTab] = useState(SECTION_TABS[section] || "1");

    // Silme onayı: hangi görselin silineceğini tutar ('logo' | 'favicon' | null).
    // Aynı modal iki alan için kullanılıyor; taksit grupları sekmesindeki desenin aynısı.
    const [pendingDelete, setPendingDelete] = useState(null);

    const confirmDelete = async () => {
        const type = pendingDelete;
        setPendingDelete(null);
        if (type) await handleImageDelete(type);
    };

    const toggle = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    return (
        <div className="company-settings-page">
            <Form onSubmit={formik.handleSubmit}>
                {/* SEKME BAŞLIKLARI */}
                <Nav tabs className="nav-tabs-custom mb-4">
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={classnames({ active: activeTab === "1" })}
                            onClick={() => toggle("1")}
                        >
                            <i className="bx bx-building-house me-2"></i>
                            Firma Bilgileri
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={classnames({ active: activeTab === "2" })}
                            onClick={() => toggle("2")}
                        >
                            <i className="bx bx-money me-2"></i>
                            Döviz Kuru Ayarları
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={classnames({ active: activeTab === "3" })}
                            onClick={() => toggle("3")}
                        >
                            <i className="bx bx-file me-2"></i>
                            Sözleşmeler
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* SEKME İÇERİKLERİ */}
                <TabContent activeTab={activeTab} className="p-3 text-muted">

                    {/* TAB 1: FİRMA BİLGİLERİ (İletişim, Adres, Logo) */}
                    <TabPane tabId="1">
                        <Row>
                            {/* Sol Kolon: Temel İletişim Bilgileri */}
                            <Col xs={12} md={6}>
                                <h5 className="font-size-14 mb-3">Temel Bilgiler</h5>
                                <FormGroup>
                                    <Label>Firma Adı *</Label>
                                    <Input
                                        name="companyName"
                                        value={formik.values.companyName}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.companyName && Boolean(formik.errors.companyName)}
                                    />
                                    {formik.touched.companyName && formik.errors.companyName && (
                                        <FormFeedback>{formik.errors.companyName}</FormFeedback>
                                    )}
                                </FormGroup>

                                <FormGroup>
                                    <Label>İletişim Kişisi *</Label>
                                    <Input
                                        name="contactFullName"
                                        value={formik.values.contactFullName}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.contactFullName && Boolean(formik.errors.contactFullName)}
                                    />
                                    {formik.touched.contactFullName && formik.errors.contactFullName && (
                                        <FormFeedback>{formik.errors.contactFullName}</FormFeedback>
                                    )}
                                </FormGroup>

                                <FormGroup>
                                    <Label>E-posta *</Label>
                                    <Input
                                        type="email"
                                        name="contactEmail"
                                        value={formik.values.contactEmail}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.contactEmail && Boolean(formik.errors.contactEmail)}
                                    />
                                    {formik.touched.contactEmail && formik.errors.contactEmail && (
                                        <FormFeedback>{formik.errors.contactEmail}</FormFeedback>
                                    )}
                                </FormGroup>

                                <FormGroup>
                                    <PhoneInput
                                        label="Telefon *"
                                        name="phoneNumber"
                                        value={formik.values.phoneNumber}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                                    />
                                    {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                                        <FormFeedback>{formik.errors.phoneNumber}</FormFeedback>
                                    )}
                                </FormGroup>
                            </Col>

                            {/* Sağ Kolon: Adres ve Vergi Bilgileri */}
                            <Col xs={12} md={6}>
                                <h5 className="font-size-14 mb-3">Adres & Vergi</h5>
                                <FormGroup>
                                    <Label>Domain *</Label>
                                    <Input
                                        name="domain"
                                        value={formik.values.domain}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.domain && Boolean(formik.errors.domain)}
                                    />
                                    {formik.touched.domain && formik.errors.domain && (
                                        <FormFeedback>{formik.errors.domain}</FormFeedback>
                                    )}
                                </FormGroup>

                                <FormGroup>
                                    <Label>Adres *</Label>
                                    <Input
                                        type="textarea"
                                        name="address"
                                        value={formik.values.address}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.address && Boolean(formik.errors.address)}
                                        rows={5}
                                    />
                                    {formik.touched.address && formik.errors.address && (
                                        <FormFeedback>{formik.errors.address}</FormFeedback>
                                    )}
                                </FormGroup>

                                <Row>
                                    {isCorporate ? (
                                        <>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label>Vergi Dairesi</Label>
                                                    <Input
                                                        type="text"
                                                        name="invoiceTaxOffice"
                                                        value={formik.values.invoiceTaxOffice}
                                                        onChange={formik.handleChange}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label>Vergi Numarası</Label>
                                                    <Input
                                                        type="text"
                                                        name="invoiceTaxNumber"
                                                        value={formik.values.invoiceTaxNumber}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                            formik.setFieldValue("invoiceTaxNumber", val);
                                                        }}
                                                        maxLength={10}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </>
                                    ) : (
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>TC Kimlik Numarası</Label>
                                                <Input
                                                    type="text"
                                                    name="invoiceTcNumber"
                                                    value={formik.values.invoiceTcNumber}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/\D/g, '').slice(0, 11);
                                                        formik.setFieldValue("invoiceTcNumber", val);
                                                    }}
                                                    maxLength={11}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>
                            </Col>
                        </Row>

                        <hr className="my-4" />

                        {/* GÖRSELLER */}
                        <Row>
                            <Col xs={12} md={6} className="text-center">
                                <h5>Logo</h5>
                                <div className="image-upload-box p-3 mb-3 d-flex align-items-center justify-content-center bg-light border rounded" style={{ height: "150px" }}>
                                    {logoBlobUrl && !logoError ? (
                                        <img
                                            src={logoBlobUrl}
                                            alt="Firma Logosu"
                                            style={{ maxWidth: "100%", maxHeight: "130px", objectFit: "contain" }}
                                            onError={() => {}}
                                        />
                                    ) : (
                                        <div className="text-muted">{logoError ? "Logo yüklenemedi" : "Logo bulunamadı"}</div>
                                    )}
                                </div>

                                {logoBlobUrl && !logoError && (
                                    <div className="d-flex flex-wrap justify-content-center gap-2 mb-2">
                                        <Button
                                            size="sm"
                                            className="btn-outline-navy btn-rounded"
                                            onClick={downloadLogo}
                                        >
                                            <i className="bx bx-download me-1"></i> Logo İndir
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="danger"
                                            outline
                                            className="btn-rounded"
                                            onClick={() => setPendingDelete('logo')}
                                        >
                                            <i className="bx bx-trash me-1"></i> Kaldır
                                        </Button>
                                    </div>
                                )}

                                <div>
                                    <Label className="btn btn-sm btn-info text-white">
                                        <i className="bx bx-upload me-1"></i> Logo Yükle
                                        <Input
                                            type="file"
                                            hidden
                                            onChange={(e) => handleImageUpload(e, 'logo')}
                                            accept={BRANDING_ACCEPT_ATTR}
                                        />
                                    </Label>
                                    <div className="text-muted small mt-2">
                                        <div><strong>Desteklenen:</strong> PNG, JPG, JPEG (Maks 5MB)</div>
                                        <div><strong>Ölçü:</strong> {formatAllowedDimensions('logo')}</div>
                                    </div>
                                </div>
                            </Col>

                            <Col xs={12} md={6} className="text-center mt-4 mt-md-0">
                                <h5>Favicon</h5>
                                <div className="image-upload-box p-3 mb-3 d-flex align-items-center justify-content-center bg-light border rounded" style={{ height: "150px" }}>
                                    {faviconBlobUrl && !faviconError ? (
                                        <img
                                            src={faviconBlobUrl}
                                            alt="Firma Faviconu"
                                            style={{ maxWidth: "100%", maxHeight: "130px", objectFit: "contain" }}
                                            onError={() => {}}
                                        />
                                    ) : (
                                        <div className="text-muted">{faviconError ? "Favicon yüklenemedi" : "Favicon bulunamadı"}</div>
                                    )}
                                </div>

                                {faviconBlobUrl && !faviconError && (
                                    <div className="d-flex flex-wrap justify-content-center gap-2 mb-2">
                                        <Button
                                            size="sm"
                                            className="btn-outline-navy btn-rounded"
                                            onClick={downloadFavicon}
                                        >
                                            <i className="bx bx-download me-1"></i> Favicon İndir
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="danger"
                                            outline
                                            className="btn-rounded"
                                            onClick={() => setPendingDelete('favicon')}
                                        >
                                            <i className="bx bx-trash me-1"></i> Kaldır
                                        </Button>
                                    </div>
                                )}

                                <div>
                                    <Label className="btn btn-sm btn-info text-white">
                                        <i className="bx bx-upload me-1"></i> Favicon Yükle
                                        <Input
                                            type="file"
                                            hidden
                                            onChange={(e) => handleImageUpload(e, 'favicon')}
                                            accept={BRANDING_ACCEPT_ATTR}
                                        />
                                    </Label>
                                    <div className="text-muted small mt-2">
                                        <div><strong>Desteklenen:</strong> PNG, JPG, JPEG (Maks 5MB)</div>
                                        <div><strong>Ölçü:</strong> {formatAllowedDimensions('favicon')}</div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </TabPane>

                    {/* TAB 2: DÖVİZ KURU AYARLARI */}
                    <TabPane tabId="2">
                        <h5 className="mb-3">Döviz Kuru Yapılandırması</h5>

                        <FormGroup switch className="mb-4 custom-switch-lg p-3 bg-light rounded border">
                            <Input
                                type="switch"
                                name="fetchRatesAutomatically"
                                checked={formik.values.fetchRatesAutomatically}
                                onChange={formik.handleChange}
                                className="ms-2" // <-- Switch'i biraz sağa (içeri) alır
                                style={{ transform: "scale(1.3)", cursor: "pointer" }} // <-- Butonu %30 büyütür
                            />
                            <Label check className="ms-3 fw-bold" style={{ cursor: "pointer" }}> {/* ms-3 ile yazı ile buton arasını açtık */}
                                Döviz Kurlarını Otomatik Belirle (TCMB + Oran)
                            </Label>
                            <FormText className="d-block mt-2 text-muted ms-1">
                                Aktifken, sistem TCMB kurları üzerine belirlediğiniz oranları ekler. Pasifken, final kur değerlerini doğrudan siz girersiniz.
                            </FormText>
                        </FormGroup>

                        {/* OTOMATİK MOD */}
                        {formik.values.fetchRatesAutomatically && (
                            <div className="animation-fade-in">
                                <h6 className="text-muted border-bottom pb-2 mb-3">Otomatik Kur İçin Ekleme Oranları (Spread)</h6>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Dolar ($) Ekleme Oranı (%)</Label>
                                            <Input
                                                type="number"
                                                name="usdMarkupRate"
                                                value={formik.values.usdMarkupRate}
                                                onChange={formik.handleChange}
                                                step="0.001"
                                                placeholder="Örn: 0.03"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Euro (€) Ekleme Oranı (%)</Label>
                                            <Input
                                                type="number"
                                                name="eurMarkupRate"
                                                value={formik.values.eurMarkupRate}
                                                onChange={formik.handleChange}
                                                step="0.001"
                                                placeholder="Örn: 0.03"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Pound (£) Ekleme Oranı (%)</Label>
                                            <Input
                                                type="number"
                                                name="gbpMarkupRate"
                                                value={formik.values.gbpMarkupRate}
                                                onChange={formik.handleChange}
                                                step="0.001"
                                                placeholder="Örn: 0.03"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {/* MANUEL MOD */}
                        {!formik.values.fetchRatesAutomatically && (
                            <div className="animation-fade-in">
                                <h6 className="text-muted border-bottom pb-2 mb-3">Manuel Final Kur Değerleri</h6>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Final Dolar ($) Kuru</Label>
                                            <Input
                                                type="number"
                                                name="finalUsdRate"
                                                value={formik.values.finalUsdRate}
                                                onChange={formik.handleChange}
                                                step="0.00001"
                                                placeholder="Örn: 32.85"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Final Euro (€) Kuru</Label>
                                            <Input
                                                type="number"
                                                name="finalEurRate"
                                                value={formik.values.finalEurRate}
                                                onChange={formik.handleChange}
                                                step="0.00001"
                                                placeholder="Örn: 35.12"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Final Pound (£) Kuru</Label>
                                            <Input
                                                type="number"
                                                name="finalGbpRate"
                                                value={formik.values.finalGbpRate}
                                                onChange={formik.handleChange}
                                                step="0.00001"
                                                placeholder="Örn: 41.50"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </TabPane>

                    {/* TAB 3: SÖZLEŞMELER */}
                    <TabPane tabId="3">
                        <Row>
                            <Col lg={12}>
                                <div className="alert alert-info">
                                    <i className="bx bx-info-circle me-1"></i>
                                    Kendi sözleşme metinlerinizi girebilirsiniz veya "Örnek Yükle" butonuna basarak varsayılan metni getirebilirsiniz.
                                </div>
                            </Col>

                            <Col lg={6}>
                                <FormGroup className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Label className="mb-0">İptal / İade Koşulları &amp; Gizlilik *</Label>
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="text-primary p-0 text-decoration-none"
                                            disabled={sampleLoading}
                                            onClick={() => loadSampleContract("cancellationReturnPrivacy")}
                                        >
                                            <i className="bx bx-download me-1"></i>Örnek Yükle
                                        </Button>
                                    </div>
                                    <Input
                                        type="textarea"
                                        rows={8}
                                        style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                                        name="cancellationReturnPrivacy"
                                        value={formik.values.cancellationReturnPrivacy}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.cancellationReturnPrivacy && !!formik.errors.cancellationReturnPrivacy}
                                    />
                                    <FormFeedback>{formik.errors.cancellationReturnPrivacy}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col lg={6}>
                                <FormGroup className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Label className="mb-0">Kişisel Veriler Politikası *</Label>
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="text-primary p-0 text-decoration-none"
                                            disabled={sampleLoading}
                                            onClick={() => loadSampleContract("personalDataPolicy")}
                                        >
                                            <i className="bx bx-download me-1"></i>Örnek Yükle
                                        </Button>
                                    </div>
                                    <Input
                                        type="textarea"
                                        rows={8}
                                        style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                                        name="personalDataPolicy"
                                        value={formik.values.personalDataPolicy}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.personalDataPolicy && !!formik.errors.personalDataPolicy}
                                    />
                                    <FormFeedback>{formik.errors.personalDataPolicy}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col lg={6}>
                                <FormGroup className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Label className="mb-0">Hizmet Sözleşmesi *</Label>
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="text-primary p-0 text-decoration-none"
                                            disabled={sampleLoading}
                                            onClick={() => loadSampleContract("serviceContract")}
                                        >
                                            <i className="bx bx-download me-1"></i>Örnek Yükle
                                        </Button>
                                    </div>
                                    <Input
                                        type="textarea"
                                        rows={8}
                                        style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                                        name="serviceContract"
                                        value={formik.values.serviceContract}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.serviceContract && !!formik.errors.serviceContract}
                                    />
                                    <FormFeedback>{formik.errors.serviceContract}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col lg={6}>
                                <FormGroup className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Label className="mb-0">Online Tahsilat Sözleşmesi *</Label>
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="text-primary p-0 text-decoration-none"
                                            disabled={sampleLoading}
                                            onClick={() => loadSampleContract("onlineCollectionContract")}
                                        >
                                            <i className="bx bx-download me-1"></i>Örnek Yükle
                                        </Button>
                                    </div>
                                    <Input
                                        type="textarea"
                                        rows={8}
                                        style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                                        name="onlineCollectionContract"
                                        value={formik.values.onlineCollectionContract}
                                        onChange={formik.handleChange}
                                        invalid={formik.touched.onlineCollectionContract && !!formik.errors.onlineCollectionContract}
                                    />
                                    <FormFeedback>{formik.errors.onlineCollectionContract}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>

                {/* SABİT KAYDET BUTONU */}
                <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                    <Button
                        className="btn-navy btn-rounded px-5 py-2"
                        disabled={loading}
                        type="submit"
                        style={{ minWidth: "150px" }}
                    >
                        {loading ? <Spinner size="sm" /> : (
                            <>
                                <i className="bx bx-save me-1 font-size-16 align-middle"></i> Değişiklikleri Kaydet
                            </>
                        )}
                    </Button>
                </div>
            </Form>

            {/* SİLME ONAY MODALI */}
            <Modal
                isOpen={pendingDelete !== null}
                toggle={() => setPendingDelete(null)}
                centered
            >
                <ModalHeader toggle={() => setPendingDelete(null)} className="border-0 pb-0">
                    Silme Onayı
                </ModalHeader>
                <ModalBody className="pt-3">
                    <p className="text-muted mb-0">
                        {pendingDelete === 'favicon'
                            ? "Faviconu kaldırmak istediğinize emin misiniz? Sekme ikonu varsayılana döner."
                            : "Logoyu kaldırmak istediğinize emin misiniz? Bu işlem geri alınamaz."}
                    </p>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button
                        color="secondary"
                        outline
                        className="btn-rounded"
                        onClick={() => setPendingDelete(null)}
                    >
                        İptal
                    </Button>
                    <Button
                        color="danger"
                        className="btn-rounded"
                        onClick={confirmDelete}
                    >
                        Kaldır
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}